import type { GuidanceScope, Portal } from '../types';

/**
 * CONTRACT §5 — whose progress this is, without saying who.
 *
 * The opaque public IDs of the scope, joined with `|` and hashed with SHA-256
 * (SubtleCrypto); the first 16 hex characters name the storage key. No ID,
 * email or name is stored — and user A's key is never user B's, so one
 * person's completion never hides another's guides.
 */
export function scopeParts(portal: Portal, scope: GuidanceScope): string[] | null {
  const parts =
    portal === 'tenant'
      ? [scope.tenantId, scope.userId]
      : portal === 'vendor'
        ? [scope.supplierId, scope.buyerId, scope.userId]
        : [scope.operatorId];

  return parts.every((part) => typeof part === 'string' && part !== '')
    ? (parts as string[])
    : null;
}

/**
 * The first 16 hex characters of SHA-256 over the parts, or `null` where the
 * browser offers no SubtleCrypto (an insecure origin): progress then stays in
 * memory for the session.
 */
export async function scopeHash(parts: readonly string[]): Promise<string | null> {
  const subtle = globalThis.crypto?.subtle;

  if (subtle === undefined || typeof subtle.digest !== 'function') return null;

  try {
    const digest = await subtle.digest('SHA-256', new TextEncoder().encode(parts.join('|')));

    return [...new Uint8Array(digest)]
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('')
      .slice(0, 16);
  } catch {
    return null;
  }
}

export function progressKey(portal: Portal, hash: string): string {
  return `fidelen.guidance.v1.${portal}.${hash}`;
}
